<template>
  <div class="page-container">
    <Header :pageName="$route.name" />
    <div class="page-element">
      <h2 class="titles">Solitude of a Scythe</h2>
      <div class="boxContainer">
        <div class="infoBox">
          <h3 class="subtitle">Concept</h3>
          <p class="desc">Inspired by Scythe by Neal Shusterman, this animation explores the emotional weight of a scythe’s existence—torn between duty and longing. The protagonist, bound by their role as a bringer of death, struggles with the isolation their position demands.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Style & Technique</h3>
          <p class="desc">This piece is not traditional animation but frame-based storytelling. Instead of smooth movement, the sequence unfolds through dissolving still frames, emphasizing the weight of time and decision.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Story & Visuals</h3>
          <p class="desc">A scythe walks down a dim corridor, approaching a desk. Lights fade—only one name remains illuminated, signaling the next victim. Ghostly figures appear, surrounding the scythe as guilt takes hold. Moving to a window, the scythe reaches out toward a passing figure, only to pull away, knowing they can never form real connections.</p>
        </div>
      </div>
      <div class="videoContainer">
        <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/YxoWqzd_FFw?si=n3xezMzjzMlhkz89" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
      </div>
      <div class="boxContainer">
        <div class="infoBox">
          <h3 class="subtitle">Voice-over</h3>
          <p class="desc">A poetic narration reinforces the theme of loneliness, using symbolic phrasing rather than direct storytelling. Something along the lines of: "I write their names... scars fill the epiderm. Unbearable memories... one more, one more, one more. I long for warmth, yet I stand against the cold wall of solitude."</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle"> Challenges & Process</h3>
          <p class="desc">The animation started with blocking out rough shapes, ensuring the composition and pacing conveyed the right emotions before refining the models. A 10mm lens was chosen to enhance the overwhelming presence of guilt (ghosts appearing in the frame).</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Reflection</h3>
          <p class="desc">The challenge was to express deep emotion with minimal animation, using light, shadow, and composition to tell a story. Does a fading light revealing a name feel too predictable? Maybe—but its simplicity allows the weight of the moment to sink in.</p>
        </div>
      </div>
    </div>
    <div class="page-element">
      <h2 class="titles">Starship Delta (pt1)</h2>
      <div class="boxContainer">
        <div class="infoBox">
          <h3 class="subtitle">Voice-over</h3>
          <p class="desc">The animation depicts a spaceship docking at a space station after a mission, preparing for the next one. The central idea is to illustrate the complexity and precision involved in this maneuver.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Style & Technique</h3>
          <p class="desc">This 3D animation employs dynamic camera movements to capture the spaceship's motion as it navigates around the rotating space station. The camera tracks the ship at various points, creating a sense of immersion and excitement. The AI's dialogue contrasts the intricate calculations happening behind the scenes with the serene exterior, emphasizing how much we take advanced technology for granted.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Story & Visuals</h3>
          <p class="desc">The storyline follows the spaceship's return to the space station. The visuals focus on the ship's approach, the docking sequence, and the interaction between the spaceship and the station’s AI. The station spins slowly, adding to the challenge of the docking maneuver. The camera angles vary to highlight the ship's trajectory, the station's rotating structure, and the overall complexity of the process.</p>
        </div>
      </div>
      <div class="videoContainer">
        <iframe width="560" height="315" src="https://www.youtube.com/embed/26kBby81ck4?si=jL9rzq87_TUVaVmA" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
      </div>
      <div class="boxContainer">
        <div class="infoBox">
          <h3 class="subtitle">Voice-over</h3>
          <p class="desc">The voice-over features the space station’s AI discussing unrelated topics, highlighting the disparity between the overwhelming technical processes and the calm, routine dialogue. This juxtaposition underscores how advanced technology seamlessly handles incredibly complex tasks.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Challenges & Process</h3>
          <p class="desc">The main challenge was achieving realistic motion while balancing the ship and station's movements. Ensuring smooth transitions and avoiding visual disorientation required meticulous keyframe animation. Using advanced camera techniques helped capture the sense of movement and scale.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Reflection</h3>
          <p class="desc">This animation was an exercise in demonstrating the hidden intricacies of space maneuvers. The contrast between the AI's dialogue and the visual complexity aimed to provoke thought about how much we overlook the sophistication of technology in our everyday lives.</p>
        </div>
      </div>
    </div>
    <div class="page-element">
      <h2 class="titles">Abduction (Starship Delta pt - 2)</h2>
      <div class="boxContainer">
        <div class="infoBox">
          <h3 class="subtitle">Story & Plot</h3>
          <p class="desc">After receiving their mission, the spaceship travels to Earth. This VFX video showcases a 3D element integrated into the real-world environment, highlighting the abduction process. The protagonist is substituted with a 3D object to create the illusion of being abducted into the spaceship.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Techniques & Process</h3>
          <p class="desc">The background was tracked in Blender to integrate the 3D elements seamlessly. Afterward, the elements were moved to After Effects to add effects such as the ring of warped-space created when the spaceship enters the atmosphere. The integration of 3D elements with real-world footage was meticulously handled to ensure realism.</p>
        </div>
        <div class="infoBox">
          <h3 class="subtitle">Sound Design</h3>
          <p class="desc">Premiere Pro was used for sound design, adding elements like spaceship sounds, sirens, and distant chaos to enhance the atmosphere. The sound design aimed to create an immersive experience, complementing the visual effects and reinforcing the narrative of the abduction.</p>
        </div>
      </div>
      <div class="videoContainer">
        <iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/DQhpeGP97pM?si=mwJSEgYT_VHvy4li" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
      </div>
    </div>
    <div class="page-element">
      <h2 class="titles">Still Life</h2>
      <p class="desc">Illuminated by the forge’s glow</p>
      <img class="forgeImage" alt="Forge Image" src="@/assets/images/allImg.webp" @click="openLightbox('@/assets/images/allImg.webp')">
      <p class="descr">Set in a stark, dim environment, this scene captures the essence of a blacksmith’s forge. The radiant glow of the forge serves as the only source of light, casting dramatic shadows over a weathered table. On this table lie the essential tools of the craft—a battle hammer, a sword accented with a delicate piece of jade at its pommel, and a set of traditional clippers—arranged against a sturdy anvil. The interplay of light and shadow creates an atmosphere that speaks to both the art of creation and the enduring legacy of craftsmanship.</p>
    </div>
    <div class="page-element">
      <h2 class="titles">Individual Objects</h2>
      <p class="desc">Explore the details behind each object that brings this scene to life.</p>
      <div class="boxContainer">
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Forge.webp" @click="openLightbox('@/assets/images/Forge.webp')">
          <h3 class="subtitle">The radiant heart of the scene.</h3>
        </div>
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Table.webp" @click="openLightbox('@/assets/images/Table.webp')"> 
          <h3 class="subtitle">A modest stage for the tools.</h3>
        </div>
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Sword.webp" @click="openLightbox('@/assets/images/Sword.webp')"> 
          <h3 class="subtitle">Embodies strength and precision.</h3>
        </div>
      </div>
      <div class="boxContainer">
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Hammer.webp" @click="openLightbox('@/assets/images/Hammer.webp')">
          <h3 class="subtitle">A refined blend of power and beauty.</h3>
        </div>
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Clippers.webp" @click="openLightbox('@/assets/images/Clippers.webp')"> 
          <h3 class="subtitle">Essential for shaping red-hot iron.</h3>
        </div>
        <div class="infoBox">
          <img class="forgeImage" alt="Forge Image" src="@/assets/images/Anvil.webp" @click="openLightbox('@/assets/images/Anvil.webp')"> 
          <h3 class="subtitle">The enduring foundation.</h3>
        </div>
      </div>
    </div>
    <LightboxComponent v-if="lightboxVisible" :image="lightboxImage" @close="closeLightbox" />
    <SiteFooter />
  </div>
</template>

<script>
import Header from './header.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import LightboxComponent from '@/components/Lightbox.vue';

export default {
  name: '3DPage',
  components: {
    SiteFooter,
    Header,
    LightboxComponent
  },
  data() {
    return {
      lightboxVisible: false,
      lightboxImage: ''
    };
  },
  methods: {
    openLightbox(image) {
      this.lightboxImage = image;
      this.lightboxVisible = true;
    },
    closeLightbox() {
      this.lightboxVisible = false;
      this.lightboxImage = '';
    }
  }
}
</script>

<style scoped lang="scss">
@use '../assets/styles/global.scss' as *;

.forgeImage {
  width: 80%;
  margin-top: 20px;
  align-self: center;
  cursor: pointer;
}

.descr {
  color: var(--Black, #26091C);
  font-family: "PT Sans Narrow";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  width: 50%;
  align-self: center;
  margin-top: 30px;
}

@media (max-width: 768px) {
  .page-container {
    padding: 10px;
  }

  .titles {
    font-size: 1.5em;
  }

  .boxContainer {
    flex-direction: column;
    align-items: center;
  }

  .infoBox {
    width: 100%;
    margin-bottom: 20px;
  }

  .videoContainer iframe {
    width: 100%;
    height: auto;
  }

  .forgeImage {
    width: 100%;
  }

  .desc, .descr {
    width: 100%;
    font-size: 14px;
    padding: 0 10px;
    box-sizing: border-box;
  }
}
</style>
